import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, CartesianGrid,
} from 'recharts';
import { adminAPI } from '../../utils/api';
import { format, subDays } from 'date-fns';

const COLORS = ['#2E539F','#5CC3BC','#FFC849','#ef4444','#8b5cf6','#10b981','#f97316','#0891b2'];

const Tooltip_ = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl p-3 shadow-xl text-xs" style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
      {label && <p className="font-semibold mb-1" style={{ color:'var(--text)' }}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color:p.color }}>{p.name}: <span className="font-bold">{p.value}</span></p>
      ))}
    </div>
  );
};

function StatCard({ label, value, sub, color, icon }) {
  return (
    <div className="card px-5 py-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-3xl font-bold" style={{ color }}>{value}</p>
          <p className="text-sm font-medium mt-0.5" style={{ color:'var(--text)' }}>{label}</p>
          {sub && <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{sub}</p>}
        </div>
        <span className="text-2xl">{icon}</span>
      </div>
    </div>
  );
}

export default function AdminAnalyticsPage() {
  const [overtime, setOvertime]   = useState([]);
  const [error, setError] = useState('');
  const [summary,  setSummary]    = useState(null);
  const [loading,  setLoading]    = useState(true);
  const [range,    setRange]      = useState(6); // months

  useEffect(() => {
    setLoading(true);
    Promise.all([
      adminAPI.getStatsOverTime(range).catch(() => ({ data:[] })),
      adminAPI.getDashboard().catch(() => ({ data:null })),
    ]).then(([ot, dash]) => {
      const rows = (ot.data||[]).map(d => ({
        ...d,
        date: d.month ? format(new Date(d.month), 'MMM yyyy') : '',
        Projects: parseInt(d.projects||0),
        Tasks:    parseInt(d.tasks||0),
        Bugs:     parseInt(d.bugs||0),
        Logins:   parseInt(d.logins||0),
      }));
      setOvertime(rows);
      setSummary(dash.data);
    }).finally(() => setLoading(false));
  }, [range]);

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
    </div>
  );

  // Build pie data from summary
  const statusPie = summary ? [
    { name:'Active Projects',    value: parseInt(summary.projects?.active||0) },
    { name:'Open Tasks',         value: parseInt(summary.tasks?.open||0) },
    { name:'Open Bugs',          value: parseInt(summary.bugs?.open||0) },
    { name:'Active Users',       value: parseInt(summary.users?.active||0) },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Analytics</h1>
          <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>
            Platform usage and activity metrics
          </p>
        </div>
        <div className="flex rounded-lg border overflow-hidden text-sm" style={{ borderColor:'var(--border)' }}>
          {[3,6,12,24].map(d=>(
            <button key={d} onClick={()=>setRange(d)}
              className={`px-3 py-1.5 font-medium transition-colors ${range===d?'bg-brand-500 text-white':''}`}
              style={range!==d?{color:'var(--text-secondary)',background:'var(--surface)'}:{}}>
              {d}m
            </button>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Users"     value={summary.users?.total||0}    sub={`${summary.users?.active||0} active`}        color="#2E539F" icon="👥"/>
          <StatCard label="Total Projects"  value={summary.projects?.total||0} sub={`${summary.projects?.active||0} active`}     color="#10b981" icon="📁"/>
          <StatCard label="Open Tasks"      value={summary.tasks?.open||0}     sub={`${summary.tasks?.total||0} total`}          color="#FFC849" icon="✅"/>
          <StatCard label="Open Bugs"       value={summary.bugs?.open||0}      sub={`${summary.bugs?.total||0} total`}           color="#ef4444" icon="🐛"/>
        </div>
      )}

      {/* Activity over time */}
      {overtime.length > 0 ? (
        <>
          <div className="card">
            <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Activity Over Time</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={overtime} margin={{ top:5, right:20, bottom:5, left:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.5}/>
                <XAxis dataKey="date" tick={{ fontSize:11, fill:'var(--text-secondary)' }} tickLine={false}/>
                <YAxis tick={{ fontSize:11, fill:'var(--text-secondary)' }} tickLine={false} axisLine={false}/>
                <Tooltip content={<Tooltip_/>}/>
                <Legend wrapperStyle={{ fontSize:12 }}/>
                <Line type="monotone" dataKey="Projects" stroke="#2E539F" strokeWidth={2} dot={false}/>
                <Line type="monotone" dataKey="Tasks"    stroke="#5CC3BC" strokeWidth={2} dot={false}/>
                <Line type="monotone" dataKey="Bugs"     stroke="#ef4444" strokeWidth={2} dot={false}/>
                <Line type="monotone" dataKey="Logins"   stroke="#FFC849" strokeWidth={2} dot={false}/>
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card">
              <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Events by Type</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={overtime.slice(-14)} margin={{ top:5, right:10, bottom:5, left:0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4}/>
                  <XAxis dataKey="date" tick={{ fontSize:10, fill:'var(--text-secondary)' }} tickLine={false}/>
                  <YAxis tick={{ fontSize:10, fill:'var(--text-secondary)' }} tickLine={false} axisLine={false}/>
                  <Tooltip content={<Tooltip_/>}/>
                  <Bar dataKey="Tasks"    fill="#5CC3BC" radius={[3,3,0,0]}/>
                  <Bar dataKey="Bugs"     fill="#ef4444" radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card">
              <h3 className="font-semibold text-sm mb-4" style={{ color:'var(--text)' }}>Platform Overview</h3>
              {statusPie.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={statusPie} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({name,value})=>`${name}: ${value}`}>
                      {statusPie.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                    </Pie>
                    <Tooltip content={<Tooltip_/>}/>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-48" style={{ color:'var(--text-secondary)' }}>
                  <p className="text-sm">No data available</p>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="card text-center py-16">
          <p className="text-4xl mb-3">📊</p>
          <p className="font-semibold" style={{ color:'var(--text)' }}>No activity data yet</p>
          <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>
            Data will appear here as users interact with the platform
          </p>
        </div>
      )}
    </div>
  );
}
